// All content adapted from Chapter 1: "Definition and Characteristics of
// Applied Behavior Analysis" (Cooper, Heron, & Heward - 3rd ed., Chapter 1).
// Quotes and BACB Task List references are reproduced from the user-supplied PDF.

export type Section = {
  id: string;
  label: string;
  short: string;
  description: string;
};

export const SECTIONS: Section[] = [
  { id: "overview",   label: "Overview",            short: "01", description: "Map of the chapter and how to study it." },
  { id: "science",    label: "Goals of Science",    short: "02", description: "Description, prediction, and control." },
  { id: "attitudes",  label: "Attitudes of Science",short: "03", description: "Six attitudes that guide all scientists." },
  { id: "history",    label: "History",             short: "04", description: "From Watson to JABA: how ABA emerged." },
  { id: "behavior",   label: "Respondent vs Operant", short: "05", description: "Skinner's two behavior classes." },
  { id: "mentalism",  label: "Mentalism & Fictions",short: "06", description: "Hypothetical constructs and explanatory fictions." },
  { id: "behaviorisms", label: "Methodological vs Radical", short: "07", description: "Two paths through behaviorism." },
  { id: "dimensions", label: "Seven Dimensions",    short: "08", description: "Baer, Wolf, & Risley (1968)." },
  { id: "traits",     label: "Other ABA Traits",    short: "09", description: "Accountable, public, doable, empowering, optimistic." },
  { id: "definition", label: "Definition of ABA",   short: "10", description: "The six-part definition." },
  { id: "domains",    label: "Four Domains",        short: "11", description: "Philosophy, EAB, ABA, practice." },
  { id: "review",     label: "Quick Review",        short: "12", description: "Condensed must-know facts." },
  { id: "flashcards", label: "Flashcards",          short: "13", description: "Term drill, self-paced." },
  { id: "quiz",       label: "Self-Test",           short: "14", description: "Multiple-choice with feedback." },
  { id: "exam",       label: "Exam Checklist",      short: "15", description: "Track what you can recite cold." },
  { id: "glossary",   label: "Glossary",            short: "16", description: "Key terms from the chapter." },
];

// --- Goals of science ----------------------------------------------------
export const SCIENCE_GOALS = [
  {
    id: "description",
    title: "Description",
    one_liner: "Accurately observe and document what happens.",
    body:
      "Systematic observation yields descriptive knowledge - a collection of facts that can be quantified, classified, and examined for relations with other facts. Descriptive findings often suggest hypotheses for further research.",
    example:
      "White's (1975) descriptive study of 104 teachers found that praise rates dropped each grade level and disapproval exceeded praise from grade 3 onward - a finding that launched decades of follow-up research.",
    icon: "Eye",
  },
  {
    id: "prediction",
    title: "Prediction",
    one_liner: "Identify correlations that let you anticipate events.",
    body:
      "When repeated observations show that two events covary, the relation - a correlation - supports predicting one event from the presence of the other. Correlation alone cannot establish causation.",
    example:
      "McKerchar & Thompson (2004) found teacher attention correlated with problem behavior in 100% of 14 preschoolers - predicting which consequences likely maintain problem behavior in classrooms.",
    icon: "TrendingUp",
  },
  {
    id: "control",
    title: "Control",
    one_liner: "Demonstrate functional relations through experimentation.",
    body:
      "The highest level of scientific understanding. A functional relation exists when a well-controlled experiment shows that a specific change in one event (dependent variable) is reliably produced by manipulating another event (independent variable), ruling out confounds.",
    example:
      'Skinner (1953): "By arranging conditions in ways specified by the laws of a system, we not only predict, we control."',
    icon: "Target",
  },
] as const;

// --- Attitudes of science -----------------------------------------------
export const ATTITUDES = [
  {
    id: "determinism",
    title: "Determinism",
    tag: "Assumption",
    one_liner: "The universe is lawful and orderly.",
    body:
      "All scientists presume that events do not occur willy-nilly; they are related in systematic ways to other physical phenomena. Determinism plays a dual role: it is the starting assumption AND the confirmation each experiment seeks.",
    quote:
      'Skinner (1953): "If we are to use the methods of science in the field of human affairs, we must assume behavior is lawful and determined."',
  },
  {
    id: "empiricism",
    title: "Empiricism",
    tag: "Prime directive",
    one_liner: "Objective observation and measurement.",
    body:
      "Knowledge built from observations that are independent of personal prejudice or private opinion - open to anyone, demanding thorough description, systematic measurement, and precise quantification. The foremost rule in behavior analysis.",
    quote:
      'Lord Kelvin: "When you cannot measure it... your knowledge is of a meagre and unsatisfactory kind."',
  },
  {
    id: "experimentation",
    title: "Experimentation",
    tag: "Basic strategy",
    one_liner: "Controlled comparison isolating one variable.",
    body:
      "An experiment is a controlled comparison of the dependent variable under two or more conditions in which only one factor - the independent variable - differs. Use of the experimental method distinguishes EAB from other modes of investigation.",
    quote:
      'Dinsmoor (2003): the experimental method serves as "a necessary condition (sine qua non)" of behavior analysis.',
  },
  {
    id: "replication",
    title: "Replication",
    tag: "Believability",
    one_liner: "Repeat experiments to confirm reliability.",
    body:
      'Repeating experiments (and IV conditions within them) is the primary method scientists use to determine the reliability and usefulness of findings. "Replication - not the infallibility or inherent honesty of scientists - is the primary reason science is a self-correcting enterprise."',
    quote:
      "Johnston & Pennypacker (1993): replication \"pervades every nook and cranny of the experimental method.\"",
  },
  {
    id: "parsimony",
    title: "Parsimony",
    tag: "Conservative value",
    one_liner: "Rule out simple explanations first.",
    body:
      'All simple, logical explanations must be ruled out - experimentally or conceptually - before more complex or abstract explanations are considered. Derived from Occam\'s Razor: do not multiply entities beyond necessity.',
    quote:
      'William of Occam: "One should not increase, beyond what is necessary, the number of entities required to explain anything."',
  },
  {
    id: "doubt",
    title: "Philosophic Doubt",
    tag: "Guiding conscience",
    one_liner: "Treat all knowledge as tentative.",
    body:
      "Scientists continually question the truthfulness of what is regarded as fact. They remain open - and actively look for evidence - that their own findings may be wrong. Extraordinary claims require extraordinary evidence.",
    quote:
      'Skinner (1979): "Regard no practice as immutable. Change and be ready to change again. Accept no eternal verity. Experiment."',
  },
] as const;

// --- History milestones -------------------------------------------------
export const HISTORY = [
  {
    year: "1913",
    event: "Watson publishes \"Psychology as the Behaviorist Views It\"",
    detail:
      "Argues psychology's proper subject is observable behavior, not states of mind. Founds Stimulus-Response (S-R) behaviorism. Made bold over-claims (\"Give me a dozen healthy infants...\") that still get used to mis-discredit behaviorism.",
    tag: "S-R Behaviorism",
  },
  {
    year: "1938",
    event: "Skinner: The Behavior of Organisms",
    detail:
      "Formal birth of the Experimental Analysis of Behavior (EAB). Distinguishes respondent and operant behavior; introduces rate of response and within-subject methodology.",
    tag: "EAB",
  },
  {
    year: "1945",
    event: "Skinner: \"my toothache is just as physical as my typewriter\"",
    detail:
      "Skinner makes private events part of behavior - distinguished only by limited accessibility, not by special properties. Seed of radical behaviorism.",
    tag: "Radical Behaviorism",
  },
  {
    year: "1949",
    event: "Fuller publishes first human operant study",
    detail:
      "Reinforced arm movement in an 18-year-old with profound disabilities - clinicians had thought him incapable of learning. He learned in four sessions.",
    tag: "First human application",
  },
  {
    year: "1953",
    event: "Skinner: Science and Human Behavior",
    detail:
      "Speculates on applying behavior principles to education, religion, government, law, and psychotherapy.",
    tag: "Foundational text",
  },
  {
    year: "1957",
    event: "Society for the Experimental Analysis of Behavior (SEAB) founded",
    detail:
      "Followed in 1958 by Journal of the Experimental Analysis of Behavior (JEAB) - outlet for basic operant research.",
    tag: "Organization",
  },
  {
    year: "1959",
    event: "Ayllon & Michael: \"The Psychiatric Nurse as a Behavioral Engineer\"",
    detail:
      "Often cited as the formal start of what would be called applied behavior analysis. Direct-care staff used operant techniques to improve functioning of state-hospital residents.",
    tag: "Birth of ABA",
  },
  {
    year: "1968",
    event: "JABA launches; Baer, Wolf, & Risley publish the Seven Dimensions",
    detail:
      "Two events that mark the formal beginning of contemporary ABA. \"Some Current Dimensions of Applied Behavior Analysis\" remains the most cited paper in the field.",
    tag: "Formal ABA",
  },
  {
    year: "1974",
    event: "Skinner: About Behaviorism",
    detail:
      'Opens with: "Behaviorism is not the science of human behavior; it is the philosophy of that science."',
    tag: "Philosophy",
  },
  {
    year: "1982",
    event: "Iwata et al. introduce functional analysis (FA) methodology",
    detail:
      "Experimental method for determining environmental variables maintaining problem behavior. Today the dominant meaning of \"functional analysis.\"",
    tag: "FA methodology",
  },
  {
    year: "1987",
    event: "Baer, Wolf, & Risley revisit the Seven Dimensions",
    detail:
      "Affirm the seven \"self-conscious guides\" still connote the dimensions of work usually called ABA.",
    tag: "Reaffirmation",
  },
  {
    year: "1998",
    event: "Behavior Analyst Certification Board (BACB) established",
    detail:
      "First BCBAs credentialed in 1999. Registered Behavior Technician (RBT) credential follows in 2014.",
    tag: "Profession",
  },
  {
    year: "2018",
    event: "BACB credentials its 30,000th behavior analyst",
    detail:
      "Membership in ABAI and affiliates surpasses 26,000 in 63 countries.",
    tag: "Growth",
  },
] as const;

// --- Respondent vs operant ----------------------------------------------
export const BEHAVIOR_COMPARE = {
  respondent: {
    title: "Respondent behavior",
    one_liner: "Reflexive. Elicited by an antecedent stimulus.",
    points: [
      "Tradition of Pavlov (1927).",
      "Antecedent stimulus + response = a reflex.",
      'Essentially involuntary - occurs whenever the eliciting stimulus is presented.',
      "Example: bright light → pupil constriction.",
      "Conditioning of respondents = Type S conditioning (Skinner's early term).",
    ],
  },
  operant: {
    title: "Operant behavior",
    one_liner: "Emitted. Selected by its consequences.",
    points: [
      "Not elicited by preceding stimuli - influenced by stimulus changes that have followed the behavior in the past.",
      "Three-term contingency S-R-S: antecedent, behavior, consequence.",
      "Consequences \"select\" the great part of learned behavior.",
      'Skinner\'s "most powerful and fundamental contribution."',
      "Conditioning of operants = Type R conditioning (Skinner's early term).",
    ],
  },
} as const;

// --- Mentalism, hypothetical constructs, explanatory fictions -----------
export const MENTALISM = {
  mentalism: {
    title: "Mentalism",
    body:
      "An approach assuming an inner, mental dimension exists that differs from the behavioral dimension and either causes or mediates behavior. Mentalism holds that adequate causal explanation must appeal to these mental phenomena. (Moore, 2003)",
  },
  hypothetical_construct: {
    title: "Hypothetical construct",
    body:
      'A "theoretical term that refers to a possibly existing, but at the moment unobserved process or entity" (Moore, 1995). Can be neither observed nor experimentally manipulated. Examples: free will, language acquisition device, storage and retrieval mechanisms for memory.',
  },
  explanatory_fiction: {
    title: "Explanatory fiction",
    body:
      'A fictitious variable that is often simply another name for the observed behavior - it contributes nothing to understanding the variables responsible for the behavior. Saying "he walks carefully because of his caution" turns the description into a circular cause.',
    example:
      "A rat presses a lever only when a light is on. Saying it \"made the association\" or \"knows\" adds nothing functional - the experimenter, not the rat, paired light and food.",
  },
} as const;

// --- Methodological vs Radical ------------------------------------------
export const BEHAVIORISMS = {
  methodological: {
    title: "Methodological behaviorism",
    points: [
      "Acknowledges mental events but considers them outside the realm of science.",
      "Restricts analysis to public events that can be observed by more than one person.",
      "Restrictive - ignores areas of major importance for understanding behavior.",
      "Discourages innovation in the science of behavior.",
    ],
  },
  radical: {
    title: "Radical behaviorism",
    points: [
      "Skinner's philosophy underlying contemporary behavior analysis.",
      "Includes ALL behavior - public and private.",
      'Private events (thoughts, feelings) are behavior, distinguished only by limited accessibility - not by special structure.',
      "Private behavior is influenced by the same kinds of variables as public behavior.",
      'The word "radical" means thoroughgoing - including private events, not "extreme."',
      "Pragmatism: the value of a statement is judged by how well it promotes effective action.",
    ],
  },
} as const;

// --- Seven Dimensions (Baer, Wolf, & Risley, 1968) ----------------------
export const DIMENSIONS = [
  {
    id: "applied",
    letter: "A",
    title: "Applied",
    one_liner: "Investigates socially significant behaviors.",
    body:
      "Targets behaviors of immediate importance to the participants and/or their significant others - social, language, academic, daily living, self-care, vocational, recreation/leisure.",
  },
  {
    id: "behavioral",
    letter: "B",
    title: "Behavioral",
    one_liner: "Precise measurement of the actual behavior.",
    body:
      "Three points: (a) the behavior chosen must be the one in need of improvement, not a proxy; (b) the behavior must be measurable, reliably and precisely; (c) when changes are observed, ask whose behavior changed - measure observers and experimenters too.",
  },
  {
    id: "analytic",
    letter: "An",
    title: "Analytic",
    one_liner: "Demonstrates a functional relation.",
    body:
      "The experimenter demonstrates a functional relation between manipulated events and a reliable change in behavior - believable evidence that the procedures produced the change. The acid-test proof of replicable cause and effect.",
  },
  {
    id: "technological",
    letter: "T",
    title: "Technological",
    one_liner: "All procedures fully described.",
    body:
      "All operative procedures are described with sufficient detail and clarity that a trained reader can replicate them and get the same results. Test: have a trained reader act out the description - any mistakes, additions, or questions mean the description is not yet technological.",
  },
  {
    id: "conceptual",
    letter: "C",
    title: "Conceptually Systematic",
    one_liner: "Procedures tied to basic principles.",
    body:
      "Procedures (and interpretations of why they work) are described in terms of the relevant principles of behavior from which they were derived. Keeps ABA as an integrated discipline rather than a \"collection of tricks.\"",
  },
  {
    id: "effective",
    letter: "E",
    title: "Effective",
    one_liner: "Produces practical improvement.",
    body:
      'Improves behavior enough to reach clinical or social significance. "Its practical importance, specifically its power in altering behavior enough to be socially important, is the essential criterion." In 1987 Baer et al. added a second test: do the changes affect the lives that the behaviors were chosen to improve?',
  },
  {
    id: "generality",
    letter: "G",
    title: "Generality",
    one_liner: "Lasts, transfers, spreads.",
    body:
      "A behavior change has generality if it (a) lasts over time, (b) appears in environments other than the one it was trained in, and/or (c) spreads to other behaviors not directly treated.",
  },
] as const;

// --- Other ABA traits (Heward, 2005) -------------------------------------
export const TRAITS = [
  {
    id: "accountable",
    title: "Accountable",
    body:
      "Commitment to effectiveness + reliance on direct, frequent measurement yields inescapable accountability. Failure is informative - \"the constant reaction to lack of progress [is] a definitive hallmark of ABA.\" (Baer, 2005)",
  },
  {
    id: "public",
    title: "Public",
    body:
      'Everything is visible and explicit - "no ephemeral, mystical, or metaphysical explanations; there are no hidden treatments; there is no magic." (Heward, 2005)',
  },
  {
    id: "doable",
    title: "Doable",
    body:
      'Teachers, parents, supervisors, and participants themselves implement effective ABA interventions. "Good old-fashioned hard work" - not arcane wizardry.',
  },
  {
    id: "empowering",
    title: "Empowering",
    body:
      "Real tools that work. Seeing data showing improvement raises practitioners' self-assurance for harder challenges ahead.",
  },
  {
    id: "optimistic",
    title: "Optimistic",
    body:
      'Behaviorism assumes roughly equal potential and locates poor outcomes in environment, not internal essence. The "Why not?" question has driven ABA from its earliest days.',
  },
] as const;

// --- The definition of ABA -----------------------------------------------
export const ABA_DEFINITION = {
  full:
    "Applied behavior analysis is the science in which tactics derived from the principles of behavior are applied systematically to improve socially significant behavior and experimentation is used to identify the variables responsible for behavior change.",
  parts: [
    { key: "science",        label: "A science",                                hint: "Guided by the attitudes and methods of scientific inquiry." },
    { key: "systematic",     label: "Applied systematically",                   hint: "Procedures are described and implemented in a systematic, technological manner." },
    { key: "principles",     label: "Tactics derived from principles of behavior", hint: "Only tactics conceptually derived from basic principles qualify." },
    { key: "social",         label: "Socially significant behavior",            hint: "The behaviors chosen matter to the participant and/or significant others." },
    { key: "improvement",    label: "Improvement",                              hint: "First goal: meaningful change in important behavior." },
    { key: "understanding",  label: "Understanding via experimentation",        hint: "Second goal: identify the variables responsible for that change." },
  ],
} as const;

// --- Four domains --------------------------------------------------------
export const DOMAINS = [
  {
    id: "radical",
    title: "Radical Behaviorism",
    province: "Theory & philosophy",
    primary: "Conceptual and philosophical analysis",
    goal: "A theoretical account of all behavior consistent with existing data",
    scope: "Widest",
    precision: "Lowest",
  },
  {
    id: "eab",
    title: "Experimental Analysis of Behavior",
    province: "Basic research",
    primary: "Design, conduct, interpret, report basic experiments",
    goal: "Discover and clarify basic principles of behavior; functional relations between behavior and controlling variables",
    scope: "Wide",
    precision: "High",
  },
  {
    id: "aba",
    title: "Applied Behavior Analysis",
    province: "Applied research",
    primary: "Design, conduct, interpret, report applied experiments",
    goal: "A technology for improving socially significant behavior; functional relations in socially significant behavior",
    scope: "Narrower",
    precision: "Higher",
  },
  {
    id: "practice",
    title: "Practice Guided by Behavior Analysis",
    province: "Helping people behave more successfully",
    primary: "Design, implement, evaluate behavior change programs",
    goal: "Improvements in the lives of participants/clients as a result of changes in their behavior",
    scope: "Narrowest",
    precision: "Maximum",
  },
] as const;

// --- Glossary ------------------------------------------------------------
export type GlossaryTerm = {
  term: string;
  short: string;
  long: string;
};

export const GLOSSARY: GlossaryTerm[] = [
  { term: "Applied behavior analysis (ABA)", short: "Science applying principles of behavior to socially significant behavior, with experimentation to identify responsible variables.",
    long: "The science in which tactics derived from the principles of behavior are applied systematically to improve socially significant behavior, and experimentation is used to identify the variables responsible for the behavior change." },
  { term: "Behaviorism", short: "The philosophy of the science of behavior.",
    long: "Not the science of human behavior itself but the philosophy of that science (Skinner, 1974). Asks whether such a science is possible, what methods it uses, whether its laws are as valid as those of physics and biology." },
  { term: "Determinism", short: "The assumption that the universe is lawful and orderly.",
    long: "All scientists presume events occur as the result of other events, not willy-nilly. Determinism is both a starting philosophical stance and the confirmation each experiment seeks." },
  { term: "Empiricism", short: "Objective observation and measurement.",
    long: "The practice of objective observation of the phenomena of interest, independent of personal prejudice or private opinion. The foremost rule in behavior analysis." },
  { term: "Experiment", short: "Controlled comparison; only one variable differs.",
    long: "A controlled comparison of some measure of the phenomenon of interest (the dependent variable) under two or more different conditions in which only one factor at a time (the independent variable) differs from one condition to another." },
  { term: "Experimental analysis of behavior (EAB)", short: "Basic-research branch of behavior analysis.",
    long: "The natural-science approach founded by Skinner (1938) for discovering orderly relations between behavior and environmental variables. Uses rate of response, repeated measurement, within-subject comparisons, and visual analysis of graphed data." },
  { term: "Explanatory fiction", short: "A fictitious cause that is just another name for the behavior.",
    long: "A fictitious variable that often is simply another name for the observed behavior and contributes nothing to a functional understanding - e.g., saying someone \"walks carefully because of his caution.\"" },
  { term: "Functional analysis", short: "Demonstration of functional relations between environment and behavior.",
    long: "Original (Skinner) sense: demonstrations of functional relations between environmental variables and behavior. Contemporary (Iwata et al., 1982) sense: an experimental methodology for determining environmental variables maintaining problem behavior." },
  { term: "Functional relation", short: "A specific change in one event reliably produced by manipulating another.",
    long: "Exists when a well-controlled experiment demonstrates that a specific change in the dependent variable is reliably produced by specific manipulations of an independent variable, and the change is unlikely due to confounding variables." },
  { term: "Hypothetical construct", short: "A possibly existing but currently unobserved process or entity.",
    long: 'A "theoretical term that refers to a possibly existing, but at the moment unobserved process or entity" (Moore, 1995). Can be neither observed nor experimentally manipulated.' },
  { term: "Mentalism", short: "Inner mental dimension assumed to cause behavior.",
    long: "An approach to behavior that assumes an inner mental dimension exists and that mental phenomena directly cause or mediate behavior. Relies on hypothetical constructs and explanatory fictions." },
  { term: "Methodological behaviorism", short: "Considers private events outside the realm of science.",
    long: "Acknowledges the existence of mental events but does not consider them in the analysis of behavior; restricts the science to publicly observable events. Restrictive and discourages innovation in the science of behavior." },
  { term: "Parsimony", short: "Rule out simple explanations first.",
    long: "Requires that all simple, logical explanations for the phenomenon be ruled out, experimentally or conceptually, before more complex or abstract explanations are considered. Derived from Occam's Razor." },
  { term: "Philosophic doubt", short: "Treat scientific knowledge as tentative.",
    long: "The scientist continually questions the truthfulness of what is regarded as fact and remains willing to set aside cherished findings when new evidence arrives." },
  { term: "Pragmatism", short: "Truth value judged by effective action.",
    long: 'The philosophical position that "the truth value of a statement is a function of how well the statement promotes effective action" (Moore, 2008). A primary criterion by which behavior analysts judge the value of their findings.' },
  { term: "Radical behaviorism", short: "Skinner's philosophy that includes private events as behavior.",
    long: "The philosophy of behavior analysis that includes all behavior - public and private. Private events such as thinking and feeling are behavior, distinguished only by limited accessibility. \"Radical\" means thoroughgoing." },
  { term: "Replication", short: "Repeating experiments to confirm reliability.",
    long: "The repeating of experiments (and of independent-variable conditions within experiments) to determine the reliability and usefulness of findings. The primary reason science is self-correcting." },
  { term: "Science", short: "Systematic approach for seeking and organizing knowledge of the natural world.",
    long: "A systematic approach to understanding natural phenomena - as evidenced by description, prediction, and control - that relies on determinism, empiricism, experimentation, replication, parsimony, and philosophic doubt." },
];

// --- Quiz items ----------------------------------------------------------
export type QuizItem = {
  id: string;
  topic: string;
  prompt: string;
  options: string[];
  correct: number; // index
  explanation: string;
};

export const QUIZ: QuizItem[] = [
  {
    id: "q1",
    topic: "Goals of science",
    prompt: "Which level of scientific understanding requires demonstrating that a manipulation reliably produces a change while ruling out confounds?",
    options: ["Description", "Prediction", "Control", "Replication"],
    correct: 2,
    explanation: "Control is achieved when a functional relation is demonstrated - a specific change in the DV reliably produced by manipulating an IV, with confounds ruled out.",
  },
  {
    id: "q2",
    topic: "Attitudes",
    prompt: "An ABA practitioner repeats an intervention across several clients to confirm it produces the same effect. Which attitude of science is most clearly illustrated?",
    options: ["Determinism", "Parsimony", "Philosophic doubt", "Replication"],
    correct: 3,
    explanation: "Replication - repeating experiments (and IV conditions within them) - is the primary method scientists use to determine reliability.",
  },
  {
    id: "q3",
    topic: "Attitudes",
    prompt: 'Choosing the simplest explanation that fits the data and only adding complexity when required is the attitude of:',
    options: ["Empiricism", "Parsimony", "Determinism", "Experimentation"],
    correct: 1,
    explanation: 'Parsimony - derived from Occam\'s Razor. Rule out simple, logical explanations before introducing complex or abstract ones.',
  },
  {
    id: "q4",
    topic: "Behavior types",
    prompt: "Pupil constriction in response to bright light is best classified as:",
    options: ["Operant behavior", "Respondent behavior", "Verbal behavior", "Private behavior"],
    correct: 1,
    explanation: "Respondent behavior is reflexive - elicited by an antecedent stimulus. The light-pupil pair forms a reflex (Pavlovian tradition).",
  },
  {
    id: "q5",
    topic: "Behaviorisms",
    prompt: "Which position considers private events outside the realm of a scientific account?",
    options: ["Radical behaviorism", "Methodological behaviorism", "Mentalism", "Structuralism"],
    correct: 1,
    explanation: "Methodological behaviorism acknowledges mental events but excludes them from the analysis of behavior, relying only on public events.",
  },
  {
    id: "q6",
    topic: "Mentalism",
    prompt: "Saying a rat presses a lever because it \"knows\" the light signals food is an example of:",
    options: ["A functional relation", "A hypothetical construct only", "An explanatory fiction", "Operant conditioning"],
    correct: 2,
    explanation: "\"Knowing\" is an explanatory fiction - another name for the behavior that adds nothing to a functional account.",
  },
  {
    id: "q7",
    topic: "Dimensions",
    prompt: "A study describes its procedures in such detail that any trained reader could replicate them. Which dimension is this?",
    options: ["Applied", "Behavioral", "Technological", "Conceptually systematic"],
    correct: 2,
    explanation: "Technological - all operative procedures are identified and described with sufficient detail and clarity to be replicated.",
  },
  {
    id: "q8",
    topic: "Dimensions",
    prompt: "A behavior change that lasts after treatment ends and shows up in untreated settings satisfies which dimension?",
    options: ["Effective", "Analytic", "Generality", "Applied"],
    correct: 2,
    explanation: "Generality - lasting over time, appearing in other environments, and/or spreading to untreated behaviors.",
  },
  {
    id: "q9",
    topic: "History",
    prompt: "Which 1968 event is the most-cited publication in ABA and outlines the field's defining dimensions?",
    options: [
      "Fuller's arm-raising study",
      "Ayllon & Michael's \"Psychiatric Nurse\" paper",
      "Baer, Wolf, & Risley's \"Some Current Dimensions of Applied Behavior Analysis\"",
      "Iwata et al.'s functional analysis methodology",
    ],
    correct: 2,
    explanation: "Baer, Wolf, & Risley (1968) recommended seven defining dimensions of ABA. It remains the field's most cited paper.",
  },
  {
    id: "q10",
    topic: "Definition",
    prompt: "Which is NOT one of the six components of the textbook's definition of ABA?",
    options: [
      "Tactics derived from principles of behavior",
      "Statistical inference as the primary analysis",
      "Socially significant behavior",
      "Experimentation to identify variables responsible for change",
    ],
    correct: 1,
    explanation: "Statistical inference is not in the definition. EAB and ABA prefer visual analysis of graphed within-subject data.",
  },
  {
    id: "q11",
    topic: "Domains",
    prompt: "Conceptual and philosophical analysis of behavior - pursuing theoretical issues - belongs primarily to which domain?",
    options: ["Radical behaviorism", "EAB", "ABA", "Practice"],
    correct: 0,
    explanation: "The radical-behaviorism (philosophical) domain conducts theoretical and conceptual analysis.",
  },
  {
    id: "q12",
    topic: "Traits",
    prompt: '"Failure is always informative" reflects which trait of ABA?',
    options: ["Optimistic", "Public", "Accountable", "Empowering"],
    correct: 2,
    explanation: "Accountability - frequent direct measurement makes successes AND failures visible, and failure drives change.",
  },
];

// --- Exam checklist (must-knows) ----------------------------------------
export const EXAM_CHECKLIST = [
  { id: "ck-goals",       label: "Define description, prediction, control with one example each." },
  { id: "ck-attitudes",   label: "Name and define the six attitudes of science." },
  { id: "ck-fn-rel",      label: "Define functional relation in your own words." },
  { id: "ck-fa-two",      label: "Distinguish the two meanings of functional analysis (Skinner; Iwata et al.)." },
  { id: "ck-respondent",  label: "Contrast respondent and operant behavior with an example each." },
  { id: "ck-mentalism",   label: "Distinguish mentalism, hypothetical construct, explanatory fiction." },
  { id: "ck-radical",     label: "Distinguish methodological behaviorism from radical behaviorism." },
  { id: "ck-watson",      label: "Summarize Watson's S-R behaviorism and its limits." },
  { id: "ck-skinner",     label: "Summarize Skinner's contribution: EAB + radical behaviorism + three-term contingency." },
  { id: "ck-1968",        label: "Recall why 1968 is the formal start of contemporary ABA." },
  { id: "ck-dim7",        label: "Recite the seven dimensions of ABA (Baer, Wolf, & Risley, 1968)." },
  { id: "ck-traits5",     label: "Name the five other ABA traits (Heward, 2005)." },
  { id: "ck-def6",        label: "Recite the six components of the textbook's definition of ABA." },
  { id: "ck-domains4",    label: "List and contrast the four domains of behavior analysis." },
  { id: "ck-bacb",        label: "Map this chapter to BACB Task List items A-1 through A-5." },
];

// --- BACB Task List Alignment -------------------------------------------
export const BACB_ALIGNMENT = [
  { code: "A-1", label: "Identify the goals of behavior analysis as a science (description, prediction, control).", sections: ["science"] },
  { code: "A-2", label: "Explain the philosophical assumptions (selectionism, determinism, parsimony, pragmatism).", sections: ["attitudes", "behaviorisms"] },
  { code: "A-3", label: "Describe and explain behavior from the perspective of radical behaviorism.", sections: ["behaviorisms", "mentalism"] },
  { code: "A-4", label: "Distinguish among behaviorism, EAB, ABA, and professional practice guided by behavior analysis.", sections: ["domains"] },
  { code: "A-5", label: "Describe and define the dimensions of applied behavior analysis (Baer, Wolf, & Risley 1968).", sections: ["dimensions"] },
];
